import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  ocdAssessments: [],
  currentOcdAssessment: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all OCD Assessments
export const fetchOcdAssessments = createAsyncThunk(
  'ocdAssessments/fetchOcdAssessments',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/ocd-assessments?patient_id=${patientId}`
        : '/ocd-assessments';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch OCD assessments'
      );
    }
  }
);

// Fetch single OCD Assessment
export const fetchOcdAssessment = createAsyncThunk(
  'ocdAssessments/fetchOcdAssessment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/ocd-assessments/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch OCD assessment'
      );
    }
  }
);

// Create OCD Assessment
export const createOcdAssessment = createAsyncThunk(
  'ocdAssessments/createOcdAssessment',
  async (assessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/ocd-assessments', assessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create OCD assessment'
      );
    }
  }
);

// Update OCD Assessment
export const updateOcdAssessment = createAsyncThunk(
  'ocdAssessments/updateOcdAssessment',
  async ({ case_id, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/ocd-assessments/case/${case_id}`, assessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update OCD assessment'
      );
    }
  }
);

// Delete OCD Assessment
export const deleteOcdAssessment = createAsyncThunk(
  'ocdAssessments/deleteOcdAssessment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/ocd-assessments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete OCD assessment'
      );
    }
  }
);

// ==================== Slice ====================
const ocdAssessmentsSlice = createSlice({
  name: 'ocdAssessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentOcdAssessment: (state, action) => {
      state.currentOcdAssessment = action.payload;
    },
    clearCurrentOcdAssessment: (state) => {
      state.currentOcdAssessment = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchOcdAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOcdAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ocdAssessments = action.payload;
      })
      .addCase(fetchOcdAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchOcdAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOcdAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOcdAssessment = action.payload;
      })
      .addCase(fetchOcdAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createOcdAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOcdAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ocdAssessments.unshift(action.payload);
      })
      .addCase(createOcdAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateOcdAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOcdAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        const updated = action.payload.data ?? action.payload;
        const index = state.ocdAssessments.findIndex(
          (item) => item.id === updated.id
        );

        if (index !== -1) {
          state.ocdAssessments[index] = updated;
        }

        if (state.currentOcdAssessment?.id === updated.id) {
          state.currentOcdAssessment = updated;
        }
      })
      .addCase(updateOcdAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteOcdAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteOcdAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        state.ocdAssessments = state.ocdAssessments.filter(
          (item) => item.id !== action.payload
        );

        if (state.currentOcdAssessment?.id === action.payload) {
          state.currentOcdAssessment = null;
        }
      })
      .addCase(deleteOcdAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentOcdAssessment,
  clearCurrentOcdAssessment,
} = ocdAssessmentsSlice.actions;

export default ocdAssessmentsSlice.reducer;